import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useUser } from '@/app/store/auth'
import { useTimezoneDisplay } from '@/contexts/TimezoneDisplayContext'
import { formatDateInUserTimezone, getEffectiveTimezone } from '@/utils/timezoneAdvanced'
import { detectUserTimezone } from '@/utils/timezone'

export function useTimezoneDate() {
    const { i18n } = useTranslation()
    const user = useUser()
    const { displayMode } = useTimezoneDisplay()
    const detectedTimezone = detectUserTimezone()

    // Get user's current language for locale
    const currentLocale = i18n.language

    // Map i18n language codes to Intl locale codes for date formatting
    // Always use Latin numerals — languages like Arabic, Japanese, Korean
    // keep their UI translations but dates stay in a universally readable format
    const localeMap: Record<string, string> = {
        'en-US': 'en-US',
        'en-GB': 'en-GB',
        'en': 'en-US',
        'es': 'es-ES',
        'fr': 'fr-FR',
        'de': 'de-DE',
        'it': 'it-IT',
        'pt': 'pt-PT',
        'pt-BR': 'pt-BR',
        'nl': 'nl-NL',
        'sv': 'sv-SE',
        'pl': 'pl-PL',
        'ja': 'en-GB',
        'ko': 'en-GB',
        'ar': 'en-GB',
        'da': 'da-DK',
    }

    const locale = localeMap[currentLocale] || 'en-GB'

    const formatDateWithTimezone = useMemo(() => {
        return (date: Date | string, options?: {
            short?: boolean
            long?: boolean
            relative?: boolean
        }) => {
            if (options?.relative) {
                // For relative time, we still need to calculate the difference
                // but format using the effective timezone
                const dateObj = typeof date === 'string' ? new Date(date) : date
                const now = new Date()
                const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000)

                if (diffInSeconds < 60) {
                    return 'Just now'
                } else if (diffInSeconds < 3600) {
                    const minutes = Math.floor(diffInSeconds / 60)
                    return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`
                } else if (diffInSeconds < 86400) {
                    const hours = Math.floor(diffInSeconds / 3600)
                    return `${hours} hour${hours !== 1 ? 's' : ''} ago`
                } else if (diffInSeconds < 2592000) {
                    const days = Math.floor(diffInSeconds / 86400)
                    return `${days} day${days !== 1 ? 's' : ''} ago`
                } else if (diffInSeconds < 31536000) {
                    const months = Math.floor(diffInSeconds / 2592000)
                    return `${months} month${months !== 1 ? 's' : ''} ago`
                } else {
                    const years = Math.floor(diffInSeconds / 31536000)
                    return `${years} year${years !== 1 ? 's' : ''} ago`
                }
            }

            if (options?.short) {
                return formatDateInUserTimezone(date, user?.timezone, displayMode, detectedTimezone, 'MMM d, yyyy h:mm a', locale)
            }

            if (options?.long) {
                return formatDateInUserTimezone(date, user?.timezone, displayMode, detectedTimezone, 'PPPP p', locale)
            }

            return formatDateInUserTimezone(date, user?.timezone, displayMode, detectedTimezone, 'PPP p', locale)
        }
    }, [user?.timezone, displayMode, detectedTimezone, locale])

    const formatDateShortMemo = useMemo(() => {
        return (date: Date | string) => formatDateInUserTimezone(date, user?.timezone, displayMode, detectedTimezone, 'MMM d, yyyy h:mm a', locale)
    }, [user?.timezone, displayMode, detectedTimezone, locale])

    const formatDateLongMemo = useMemo(() => {
        return (date: Date | string) => formatDateInUserTimezone(date, user?.timezone, displayMode, detectedTimezone, 'PPPP p', locale)
    }, [user?.timezone, displayMode, detectedTimezone, locale])

    const formatRelativeTimeMemo = useMemo(() => {
        return (date: Date | string) => {
            const dateObj = typeof date === 'string' ? new Date(date) : date
            const now = new Date()
            const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000)

            if (diffInSeconds < 60) {
                return 'Just now'
            } else if (diffInSeconds < 3600) {
                const minutes = Math.floor(diffInSeconds / 60)
                return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`
            } else if (diffInSeconds < 86400) {
                const hours = Math.floor(diffInSeconds / 3600)
                return `${hours} hour${hours !== 1 ? 's' : ''} ago`
            } else if (diffInSeconds < 2592000) {
                const days = Math.floor(diffInSeconds / 86400)
                return `${days} day${days !== 1 ? 's' : ''} ago`
            } else if (diffInSeconds < 31536000) {
                const months = Math.floor(diffInSeconds / 2592000)
                return `${months} month${months !== 1 ? 's' : ''} ago`
            } else {
                const years = Math.floor(diffInSeconds / 31536000)
                return `${years} year${years !== 1 ? 's' : ''} ago`
            }
        }
    }, [])

    return {
        formatDate: formatDateWithTimezone,
        formatDateShort: formatDateShortMemo,
        formatDateLong: formatDateLongMemo,
        formatRelativeTime: formatRelativeTimeMemo,
        userTimezone: user?.timezone,
        displayMode
    }
}
